import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Search, Download, ExternalLink, DollarSign, MapPin, Calendar, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export default function Organizations() {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, per_page: 20, total: 0, pages: 0 });
  const [filters, setFilters] = useState({
    query: '',
    state: 'all',
    year: 'all',
    minRevenue: ''
  });

  const fetchOrganizations = async (page = 1) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '20',
        economic_dev_only: 'true'
      });
      
      if (filters.query) params.append('query', filters.query);
      if (filters.state && filters.state !== 'all') params.append('state', filters.state);
      if (filters.year && filters.year !== 'all') params.append('year', filters.year);
      if (filters.minRevenue) params.append('min_revenue', filters.minRevenue);
      
      const response = await axios.get(`${API}/organizations?${params}`);
      const { organizations: orgs, pagination: pag } = response.data;
      
      setOrganizations(orgs);
      setPagination(pag);
      
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgDonors = async (ein) => {
    try {
      setDialogLoading(true);
      const response = await axios.get(`${API}/organizations/${ein}/donors`);
      setDonors(response.data);
    } catch (error) {
      console.error('Error fetching donors:', error);
      toast.error('Failed to load donor data');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({
        format: 'csv',
        economic_dev_only: 'true'
      });
      
      if (filters.query) params.append('query', filters.query);
      
      const response = await axios.get(`${API}/export/organizations?${params}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'economic_dev_organizations.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export completed successfully');
      
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchOrganizations(1);
  };

  const handleOrgClick = (org) => {
    setSelectedOrg(org);
    fetchOrgDonors(org.ein);
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  return (
    <div className="min-h-screen p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-black tracking-tight text-slate-900 mb-2">
          Economic Development Organizations
        </h1>
        <p className="text-slate-600 max-w-3xl">
          Explore nonprofits focused on entrepreneurship, business development, microfinance, 
          and economic empowerment. View their missions, financials, and donor networks.
        </p>
      </div>

      {/* Search and Filters */}
      <Card className="mb-6 high-contrast" data-testid="search-filters-card">
        <CardHeader>
          <CardTitle className="text-lg font-bold">Search & Filter Organizations</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="flex gap-4 flex-wrap">
              <div className="flex-1 min-w-64">
                <Input
                  placeholder="Search organizations, missions, or keywords..."
                  value={filters.query}
                  onChange={(e) => setFilters(prev => ({ ...prev, query: e.target.value }))}
                  className="focus-ring"
                  data-testid="search-input"
                />
              </div>
              
              <Select value={filters.state} onValueChange={(value) => setFilters(prev => ({ ...prev, state: value }))}>
                <SelectTrigger className="w-32" data-testid="state-filter">
                  <SelectValue placeholder="State" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {US_STATES.map(state => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filters.year} onValueChange={(value) => setFilters(prev => ({ ...prev, year: value }))}>
                <SelectTrigger className="w-32" data-testid="year-filter">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  <SelectItem value="2023">2023</SelectItem>
                  <SelectItem value="2022">2022</SelectItem>
                  <SelectItem value="2021">2021</SelectItem>
                </SelectContent>
              </Select>
              
              <Input
                placeholder="Min Revenue"
                value={filters.minRevenue}
                onChange={(e) => setFilters(prev => ({ ...prev, minRevenue: e.target.value }))}
                className="w-32"
                type="number"
                data-testid="revenue-filter"
              />
            </div>
            
            <div className="flex gap-2">
              <Button type="submit" data-testid="search-btn">
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
              
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleExport}
                data-testid="export-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Organizations Table */}
      <Card className="high-contrast" data-testid="organizations-table-card">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg font-bold">
              Organizations ({pagination.total.toLocaleString()})
            </CardTitle>
            <div className="text-sm text-slate-600 data-text">
              Page {pagination.page} of {pagination.pages}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-slate-100 rounded animate-pulse"></div>
              ))}
            </div>
          ) : organizations.length > 0 ? (
            <div className="overflow-x-auto">
              <Table className="table-swiss">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-64">Organization</TableHead>
                    <TableHead>Mission Focus</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-center">Year</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {organizations.map((org, index) => (
                    <TableRow key={org.ein || index} className="cursor-pointer hover:bg-slate-50">
                      <TableCell>
                        <div>
                          <div className="font-medium text-slate-900">{org.name}</div>
                          <div className="text-sm text-slate-500 data-text">{org.ein}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-96">
                          <div className="flex flex-wrap gap-1 mb-1">
                            {org.mission_keywords?.slice(0, 3).map((keyword, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {keyword}
                              </Badge>
                            ))}
                          </div>
                          <p className="text-sm text-slate-600 line-clamp-2">
                            {org.mission_statement?.substring(0, 120)}...
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right data-text font-medium">
                        {org.total_revenue ? 
                          `$${(org.total_revenue / 1000000).toFixed(1)}M` : 
                          'N/A'
                        }
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center text-sm text-slate-600">
                          <MapPin className="h-3 w-3 mr-1" />
                          {org.city}, {org.state}
                        </div>
                      </TableCell>
                      <TableCell className="text-center data-text font-medium">
                        {org.filing_year}
                      </TableCell>
                      <TableCell>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleOrgClick(org)}
                              data-testid={`view-org-${org.ein}`}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle className="text-2xl font-bold">
                                {selectedOrg?.name}
                              </DialogTitle>
                            </DialogHeader>
                            
                            {selectedOrg && (
                              <div className="space-y-6">
                                {/* Organization Details */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <Card>
                                    <CardContent className="p-4">
                                      <div className="flex items-center space-x-2 mb-2">
                                        <DollarSign className="h-4 w-4 text-green-600" />
                                        <span className="text-sm font-medium">Revenue</span>
                                      </div>
                                      <div className="data-text font-bold text-lg">
                                        ${selectedOrg.total_revenue ? (selectedOrg.total_revenue / 1000000).toFixed(1) : 0}M
                                      </div>
                                    </CardContent>
                                  </Card>
                                  
                                  <Card>
                                    <CardContent className="p-4">
                                      <div className="flex items-center space-x-2 mb-2">
                                        <MapPin className="h-4 w-4 text-blue-600" />
                                        <span className="text-sm font-medium">Location</span>
                                      </div>
                                      <div className="font-medium">
                                        {selectedOrg.city}, {selectedOrg.state}
                                      </div>
                                    </CardContent>
                                  </Card>
                                  
                                  <Card>
                                    <CardContent className="p-4">
                                      <div className="flex items-center space-x-2 mb-2">
                                        <Calendar className="h-4 w-4 text-purple-600" />
                                        <span className="text-sm font-medium">Filing Year</span>
                                      </div>
                                      <div className="data-text font-bold text-lg">
                                        {selectedOrg.filing_year}
                                      </div>
                                    </CardContent>
                                  </Card>
                                </div>
                                
                                {/* Mission Statement */}
                                <Card>
                                  <CardHeader>
                                    <CardTitle className="text-lg">Mission Statement</CardTitle>
                                  </CardHeader>
                                  <CardContent>
                                    <p className="text-slate-700 leading-relaxed">
                                      {selectedOrg.mission_statement || 'Mission statement not available.'}
                                    </p>
                                    
                                    {selectedOrg.mission_keywords && selectedOrg.mission_keywords.length > 0 && (
                                      <div className="mt-4">
                                        <p className="text-sm font-medium text-slate-600 mb-2">Focus Areas:</p>
                                        <div className="flex flex-wrap gap-2">
                                          {selectedOrg.mission_keywords.map((keyword, idx) => (
                                            <Badge key={idx} variant="outline">
                                              {keyword}
                                            </Badge>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </CardContent>
                                </Card>
                                
                                {/* Donors */}
                                <Card>
                                  <CardHeader>
                                    <CardTitle className="text-lg flex items-center">
                                      <Users className="h-5 w-5 mr-2" />
                                      Major Donors ({donors.length})
                                    </CardTitle>
                                  </CardHeader>
                                  <CardContent>
                                    {dialogLoading ? (
                                      <div className="space-y-2">
                                        {[...Array(3)].map((_, i) => (
                                          <div key={i} className="h-12 bg-slate-100 rounded animate-pulse"></div>
                                        ))}
                                      </div>
                                    ) : donors.length > 0 ? (
                                      <div className="space-y-3 max-h-64 overflow-y-auto">
                                        {donors.map((donation, idx) => (
                                          <div key={idx} className="flex justify-between items-center p-3 bg-slate-50 rounded">
                                            <div>
                                              <div className="font-medium">
                                                {donation.donor_name || 'Anonymous Donor'}
                                              </div>
                                              {donation.donor_address && (
                                                <div className="text-sm text-slate-600">
                                                  {donation.donor_address}
                                                </div>
                                              )}
                                            </div>
                                            <div className="text-right">
                                              <div className="font-bold data-text">
                                                {donation.amount ? 
                                                  `$${donation.amount.toLocaleString()}` : 
                                                  'Amount not disclosed'
                                                }
                                              </div>
                                              <div className="text-sm text-slate-600 data-text">
                                                {donation.filing_year}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <p className="text-slate-500 text-center py-8">
                                        No donor information available for this organization.
                                      </p>
                                    )}
                                  </CardContent>
                                </Card>
                              </div>
                            )}
                          </DialogContent>
                        </Dialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Search className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No organizations found</h3>
              <p className="text-slate-500">
                Try adjusting your search criteria or process new IRS data.
              </p>
            </div>
          )}
          
          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex justify-between items-center mt-6">
              <Button 
                variant="outline" 
                onClick={() => fetchOrganizations(pagination.page - 1)}
                disabled={pagination.page <= 1}
                data-testid="prev-page-btn"
              >
                Previous
              </Button>
              
              <span className="text-sm text-slate-600 data-text">
                Showing {((pagination.page - 1) * pagination.per_page) + 1} to {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total}
              </span>
              
              <Button 
                variant="outline"
                onClick={() => fetchOrganizations(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                data-testid="next-page-btn"
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
